import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";
import { cn } from "../lib/cn";

export interface StatTileProps {
  label: string;
  /** The target numeric value the big number counts up to. */
  value: number;
  /** Decimal places to render (e.g. body-fat % → 1). */
  decimals?: number;
  prefix?: string;
  suffix?: string;
  /** Small delta pill, e.g. "+12%". Tone drives its colour. */
  delta?: string;
  deltaTone?: "up" | "down" | "flat";
  icon?: ReactNode;
  /** Pulse accent for the gradient edge / icon chip on light tiles. */
  accent?: "coral" | "violet" | "ocean" | "lime";
  tone?: "light" | "ink" | "volt";
  className?: string;
}

const tones = {
  // Punchy white KPI tile with a vivid gradient EDGE (Pulse signature).
  light: "bg-surface text-body ring-1 ring-line shadow-sm",
  // Deep ink KPI surface for high-contrast panels.
  ink: "bg-ink-800 text-ondark ring-1 ring-ink-line shadow-md",
  // Full coral→orange gradient KPI tile with a coral glow.
  volt: "bg-grad-coral text-white shadow-volt",
};

// Gradient edge + icon-chip backgrounds per accent (light tiles only).
const accentGrad = {
  coral: "bg-grad-coral",
  violet: "bg-grad-violet",
  ocean: "bg-grad-ocean",
  lime: "bg-grad-lime",
} as const;

const deltaTones = {
  up: "bg-lime-soft text-lime-600",
  down: "bg-coral-soft text-volt-600",
  flat: "bg-surface-sunken text-body-soft",
};

/**
 * A KPI surface with a big, animated count-up number.
 *
 * Robustness (P1-7 fix — never show a misleading "0"):
 *   • The FINAL value is what the DOM renders by default and what we expose to
 *     assistive tech (aria-label on the wrapper), so a glance, a screenshot, or
 *     a screen reader always sees the real number — even before any animation.
 *   • Under prefers-reduced-motion we snap straight to the final value.
 *   • The count-up tween runs when the tile scrolls into view OR, as a fallback,
 *     shortly after mount (so a tile that never reaches the in-view threshold —
 *     short phone, clipped bottom, or rendered off-screen like the login aside —
 *     still animates to the final value instead of stalling at zero).
 *   • The animated text is aria-hidden (it's a visual flourish); AT reads the
 *     stable final value from the wrapper label.
 */
export function StatTile({
  label,
  value,
  decimals = 0,
  prefix = "",
  suffix = "",
  delta,
  deltaTone = "up",
  icon,
  accent = "coral",
  tone = "light",
  className,
}: StatTileProps) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Lower threshold + a mount fallback below so partially-visible tiles count.
  const inView = useInView(wrapRef, { once: true, amount: 0.2 });
  const reduce = useReducedMotion();
  const [done, setDone] = useState(false);
  // Flips true once the count-up has been kicked off (by in-view or fallback),
  // so we never re-run the tween and never reset back to "0".
  const startedRef = useRef(false);

  const format = (n: number) =>
    `${prefix}${n.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`;
  const finalText = format(value);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    // Reduced motion: snap to the final value immediately, expose it, stop.
    if (reduce) {
      node.textContent = finalText;
      setDone(true);
      return;
    }

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const controls = animate(0, value, {
        duration: 1.1,
        ease: [0.22, 0.61, 0.36, 1],
        onUpdate: (latest) => {
          node.textContent = format(latest);
        },
        onComplete: () => {
          // Guarantee the exact final value (no float drift) + mark done.
          node.textContent = finalText;
          setDone(true);
        },
      });
      cleanup = () => controls.stop();
    };

    let cleanup: (() => void) | undefined;
    if (inView) {
      run();
    } else {
      // Fallback: animate on mount even if we never scroll into view, so the
      // tile never rests on a misleading "0". Short delay lets a scroll reveal
      // win first when the tile IS about to enter the viewport.
      const id = window.setTimeout(run, 600);
      cleanup = () => window.clearTimeout(id);
    }
    return () => cleanup?.();
    // finalText/format derive from value/decimals/prefix/suffix.
  }, [inView, reduce, value, decimals, prefix, suffix, finalText]);

  const isLight = tone === "light";

  return (
    <div
      ref={wrapRef}
      className={cn(
        "relative overflow-hidden rounded-lg p-6 transition-all duration-200 ease-kinetic",
        tones[tone],
        isLight && "hover:-translate-y-1 hover:shadow-md",
        className,
      )}
    >
      {/* Vivid gradient edge — the punchy Pulse KPI signature. */}
      {isLight && (
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute left-0 top-0 bottom-0 w-1.5 rounded-r-md",
            accentGrad[accent],
          )}
        />
      )}

      <div className="flex items-center justify-between">
        <span
          className={cn(
            "font-display text-xs font-bold uppercase tracking-kicker",
            isLight ? "text-body-soft" : "opacity-80",
          )}
        >
          {label}
        </span>
        {icon && (
          <span
            className={cn(
              "grid h-11 w-11 place-items-center rounded-md text-white shadow-sm",
              isLight ? accentGrad[accent] : "bg-white/15",
            )}
          >
            {icon}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
        {/* The number. AT reads the stable final value (aria-label); the visible
            text node is aria-hidden and may show the count-up tween. The default
            child is the FINAL value, so the first paint / any non-animating state
            shows the real number — never a misleading 0. */}
        <span
          ref={nodeRef}
          aria-label={finalText}
          className={cn(
            "nums font-display font-extrabold leading-none tracking-tightest whitespace-nowrap",
            "text-[clamp(2.1rem,1.3rem+2.6vw,3.2rem)]",
            done && tone === "light" && "transition-colors",
          )}
        >
          <span aria-hidden="true">{finalText}</span>
        </span>
        {delta && (
          <span
            className={cn(
              "mb-1 rounded-pill px-2.5 py-0.5 text-xs font-bold",
              tone === "volt"
                ? "bg-white/20 text-white"
                : deltaTones[deltaTone],
            )}
          >
            {delta}
          </span>
        )}
      </div>
    </div>
  );
}
